import moment from 'moment';
import { memo, useCallback, useMemo, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row } from 'react-bootstrap';
import DateTimePicker from 'react-datetime';

import { ItemAvailability, LevelRewardInput, MarathonMilestoneTemplate } from '../../../../../../__gqltypes__';
import { applyChangeIfDateValid, validIfSameDayOrAfter } from '../../../../../../utils/datepicker';
import EditRewardsModal from '../../../components/EditRewardsModal';
import {
  formatEventDate,
  getMilestoneRowErrors,
  MGroup,
  milestoneConfigToRuntime,
  MMilestone,
  newGroup,
  newMilestone,
  nextMilestoneValue,
  relativeDateHint,
} from '../utils';
import { MarathonMilestonesTable } from './MarathonMilestonesTable';

export type GroupsUpdater = (updater: (prev: MGroup[]) => MGroup[]) => void;

type GroupCardProps = {
  group: MGroup;
  groupIndex: number;
  isLast: boolean;
  // Highest milestone value of all previous groups (strictly-increasing validation)
  prevMaxValue: number;
  prevUnlockDate: Date | null;
  templates: MarathonMilestoneTemplate[];
  onChangeUnlockDate: (groupIndex: number, value: Date) => void;
  onChangeStopDate: (groupIndex: number, value: Date) => void;
  onChangeMilestone: (groupIndex: number, index: number, patch: Partial<MMilestone>) => void;
  onEditReward: (groupIndex: number, index: number, isPlus: boolean) => void;
  onRemoveMilestone: (groupIndex: number, index: number) => void;
  onAddMilestone: (groupIndex: number) => void;
  onClearMilestones: (groupIndex: number) => void;
  onLoadTemplate: (groupIndex: number, templateId: string) => void;
  onRemoveGroup: (groupIndex: number) => void;
};

const NO_TEMPLATE = 'None';

const MarathonGroupCard = memo(function MarathonGroupCard({
  group,
  groupIndex,
  isLast,
  prevMaxValue,
  prevUnlockDate,
  templates,
  onChangeUnlockDate,
  onChangeStopDate,
  onChangeMilestone,
  onEditReward,
  onRemoveMilestone,
  onAddMilestone,
  onClearMilestones,
  onLoadTemplate,
  onRemoveGroup,
}: GroupCardProps): JSX.Element {
  const rowErrors = useMemo(
    () => getMilestoneRowErrors(group.milestones, prevMaxValue),
    [group.milestones, prevMaxValue]
  );

  const unlockDateError =
    prevUnlockDate && !moment(group.unlockDate).isAfter(prevUnlockDate)
      ? `Must be after the previous group's unlock (${formatEventDate(prevUnlockDate)})`
      : null;
  const stopDateError =
    isLast && !moment(group.stopDate).isAfter(group.unlockDate) ? 'Must be after this group\'s unlock date' : null;

  const firstValue = group.milestones[0]?.numberOfGuesses;
  const lastValue = group.milestones[group.milestones.length - 1]?.numberOfGuesses;
  const rangeLabel =
    group.milestones.length > 0 ? `Milestones ${firstValue} – ${lastValue}` : 'No milestones';

  const handleChangeMilestone = useCallback(
    (index: number, patch: Partial<MMilestone>) => onChangeMilestone(groupIndex, index, patch),
    [onChangeMilestone, groupIndex]
  );
  const handleEditReward = useCallback(
    (index: number, isPlus: boolean) => onEditReward(groupIndex, index, isPlus),
    [onEditReward, groupIndex]
  );
  const handleRemoveMilestone = useCallback(
    (index: number) => onRemoveMilestone(groupIndex, index),
    [onRemoveMilestone, groupIndex]
  );

  return (
    <Card style={{ width: '100%', marginBottom: 10 }}>
      <Card.Header>
        <Row className="align-items-center">
          <Col>
            <Card.Title className="mb-0">
              Group {groupIndex + 1} <small className="text-muted">— {rangeLabel}</small>
            </Card.Title>
          </Col>
          <Col md="auto">
            <Button variant="outline-danger" size="sm" onClick={() => onRemoveGroup(groupIndex)}>
              <i className="fas fa-trash" /> Remove group
            </Button>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={4}>
            <Form.Label>Unlocks at (local time):</Form.Label>
            <DateTimePicker
              dateFormat="YYYY-MM-DD"
              timeFormat="HH:mm"
              // @ts-ignore
              onChange={applyChangeIfDateValid(
                (value: moment.Moment) => onChangeUnlockDate(groupIndex, value.toDate())
              )}
              isValidDate={validIfSameDayOrAfter}
              // @ts-ignore
              value={group.unlockDate}
            />
            {unlockDateError ? (
              <Form.Text className="text-danger">{unlockDateError}</Form.Text>
            ) : (
              <Form.Text muted>
                {formatEventDate(group.unlockDate)} — starts {relativeDateHint(group.unlockDate)}
              </Form.Text>
            )}
          </Col>
          <Col md={4}>
            {isLast ? (
              // Only the LAST group's stopDate is meaningful: it is the event end.
              <>
                <Form.Label>Event ends at (local time):</Form.Label>
                <DateTimePicker
                  dateFormat="YYYY-MM-DD"
                  timeFormat="HH:mm"
                  // @ts-ignore
                  onChange={applyChangeIfDateValid(
                    (value: moment.Moment) => onChangeStopDate(groupIndex, value.toDate())
                  )}
                  isValidDate={validIfSameDayOrAfter}
                  // @ts-ignore
                  value={group.stopDate}
                />
                {stopDateError ? (
                  <Form.Text className="text-danger">{stopDateError}</Form.Text>
                ) : (
                  <Form.Text muted>
                    Whole event ends {formatEventDate(group.stopDate)} ({relativeDateHint(group.stopDate)})
                  </Form.Text>
                )}
              </>
            ) : (
              // A non-last group has no "close" of its own. Its milestones unlock at the date on the
              // left and stay available until the event ends; the server gates availability only on
              // unlockDate + the event-level stop (see utils.groupsToInput / backend getUnlockedCeiling),
              // so we deliberately do NOT surface a per-group "stop date" here — that label wrongly
              // implied the group closed when the next one unlocked.
              <>
                <Form.Label>Availability:</Form.Label>
                <Form.Text className="d-block text-muted">
                  Unlocks at the time on the left, then stays available until the event ends.
                </Form.Text>
              </>
            )}
          </Col>
          <Col md={4}>
            <Form.Label>Load template into this group:</Form.Label>
            <Form.Control
              as="select"
              value={NO_TEMPLATE}
              onChange={(e) => {
                if (e.target.value !== NO_TEMPLATE) {
                  onLoadTemplate(groupIndex, e.target.value);
                }
              }}
            >
              <option key={NO_TEMPLATE} value={NO_TEMPLATE}>
                {NO_TEMPLATE}
              </option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </Form.Control>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <MarathonMilestonesTable
          milestones={group.milestones}
          rowErrors={rowErrors}
          onChangeMilestone={handleChangeMilestone}
          onEditReward={handleEditReward}
          onRemoveRow={handleRemoveMilestone}
        />
        <Row className="justify-content-center">
          <Button className="mr-2" variant="success" size="sm" onClick={() => onAddMilestone(groupIndex)}>
            <i className="fas fa-plus" /> Add Milestone
          </Button>
          <Button variant="danger" size="sm" onClick={() => onClearMilestones(groupIndex)}>
            <i className="fas fa-trash" /> Clear Milestones
          </Button>
        </Row>
      </Card.Body>
    </Card>
  );
});

type RewardTarget = {
  groupIndex: number;
  milestoneIndex: number;
  isPlus: boolean;
};

type Props = {
  groups: MGroup[];
  templates: MarathonMilestoneTemplate[];
  onUpdateGroups: GroupsUpdater;
};

export function MarathonGroupsData({ groups, templates, onUpdateGroups }: Props): JSX.Element {
  const [rewardTarget, setRewardTarget] = useState<RewardTarget | null>(null);

  const updateGroup = useCallback(
    (groupIndex: number, update: (group: MGroup) => MGroup) => {
      onUpdateGroups((prev) => prev.map((group, index) => (index === groupIndex ? update(group) : group)));
    },
    [onUpdateGroups]
  );

  const handleChangeUnlockDate = useCallback(
    (groupIndex: number, value: Date) => {
      updateGroup(groupIndex, (group) => ({ ...group, unlockDate: value }));
    },
    [updateGroup]
  );

  const handleChangeStopDate = useCallback(
    (groupIndex: number, value: Date) => {
      updateGroup(groupIndex, (group) => ({ ...group, stopDate: value }));
    },
    [updateGroup]
  );

  const handleChangeMilestone = useCallback(
    (groupIndex: number, index: number, patch: Partial<MMilestone>) => {
      updateGroup(groupIndex, (group) => ({
        ...group,
        milestones: group.milestones.map((mile, i) => (i === index ? { ...mile, ...patch } : mile)),
      }));
    },
    [updateGroup]
  );

  const handleEditReward = useCallback((groupIndex: number, milestoneIndex: number, isPlus: boolean) => {
    setRewardTarget({ groupIndex, milestoneIndex, isPlus });
  }, []);

  const handleSaveReward = useCallback(
    (reward: LevelRewardInput) => {
      if (!rewardTarget) {
        return;
      }
      const { groupIndex, milestoneIndex, isPlus } = rewardTarget;
      handleChangeMilestone(
        groupIndex,
        milestoneIndex,
        isPlus ? { plusReward: { ...reward } } : { basicReward: { ...reward } }
      );
      setRewardTarget(null);
    },
    [rewardTarget, handleChangeMilestone]
  );

  const handleRemoveMilestone = useCallback(
    (groupIndex: number, index: number) => {
      if (window.confirm(`Remove milestone ${index + 1} of group ${groupIndex + 1}?`)) {
        updateGroup(groupIndex, (group) => ({
          ...group,
          milestones: group.milestones.filter((_, i) => i !== index),
        }));
      }
    },
    [updateGroup]
  );

  const handleAddMilestone = useCallback(
    (groupIndex: number) => {
      onUpdateGroups((prev) =>
        prev.map((group, index) =>
          index === groupIndex
            ? { ...group, milestones: group.milestones.concat(newMilestone(nextMilestoneValue(prev, groupIndex))) }
            : group
        )
      );
    },
    [onUpdateGroups]
  );

  const handleClearMilestones = useCallback(
    (groupIndex: number) => {
      if (window.confirm(`Clear all milestones of group ${groupIndex + 1}?`)) {
        updateGroup(groupIndex, (group) => ({ ...group, milestones: [] }));
      }
    },
    [updateGroup]
  );

  const handleLoadTemplate = useCallback(
    (groupIndex: number, templateId: string) => {
      const template = templates.find((t) => t.id === templateId);
      if (!template) {
        return;
      }
      if (window.confirm(`Replace the milestones of group ${groupIndex + 1} with template "${template.name}"?`)) {
        updateGroup(groupIndex, (group) => ({ ...group, milestones: milestoneConfigToRuntime(template.milestones) }));
      }
    },
    [templates, updateGroup]
  );

  const handleAddGroup = useCallback(() => {
    onUpdateGroups((prev) => prev.concat(newGroup(prev)));
  }, [onUpdateGroups]);

  const handleRemoveGroup = useCallback(
    (groupIndex: number) => {
      if (window.confirm(`Remove group ${groupIndex + 1} and all its milestones?`)) {
        onUpdateGroups((prev) => prev.filter((_, index) => index !== groupIndex));
      }
    },
    [onUpdateGroups]
  );

  const selectedReward = useMemo<LevelRewardInput | null>(() => {
    if (!rewardTarget) {
      return null;
    }
    const milestone = groups[rewardTarget.groupIndex]?.milestones[rewardTarget.milestoneIndex];
    if (!milestone) {
      return null;
    }
    return rewardTarget.isPlus ? milestone.plusReward : milestone.basicReward;
  }, [groups, rewardTarget]);

  // Boundary values used by each card for validation/defaults
  const prevMaxValues = useMemo(() => {
    const values: number[] = [];
    let max = 0;
    groups.forEach((group) => {
      values.push(max);
      group.milestones.forEach((mile) => {
        max = Math.max(max, mile.numberOfGuesses);
      });
    });
    return values;
  }, [groups]);

  return (
    <>
      <EditRewardsModal
        isEdit={true}
        tier={null}
        show={rewardTarget !== null}
        level={0}
        reward={selectedReward}
        handleSave={handleSaveReward}
        handleClose={() => setRewardTarget(null)}
        disableCurrencySpecialEvent
        disableMonthlyBuff
        disableSubscriptionDay
        enableItemLevelEdit
        liveOpsAvailabilities={[ItemAvailability.EARN]}
      />
      <Alert variant="info">
        Each group&apos;s milestones become available at its <strong>unlock time</strong> and stay available
        until the <strong>event ends</strong>.
      </Alert>
      {groups.map((group, index) => (
        <MarathonGroupCard
          key={group._clientId}
          group={group}
          groupIndex={index}
          isLast={index === groups.length - 1}
          prevMaxValue={prevMaxValues[index]}
          prevUnlockDate={index > 0 ? groups[index - 1].unlockDate : null}
          templates={templates}
          onChangeUnlockDate={handleChangeUnlockDate}
          onChangeStopDate={handleChangeStopDate}
          onChangeMilestone={handleChangeMilestone}
          onEditReward={handleEditReward}
          onRemoveMilestone={handleRemoveMilestone}
          onAddMilestone={handleAddMilestone}
          onClearMilestones={handleClearMilestones}
          onLoadTemplate={handleLoadTemplate}
          onRemoveGroup={handleRemoveGroup}
        />
      ))}
      <Row className="justify-content-center mb-3">
        <Button variant="success" onClick={handleAddGroup}>
          <i className="fas fa-plus" /> Add Group
        </Button>
      </Row>
    </>
  );
}
